import { DeleteRegular } from '@fluentui/react-icons';
import { UserListSorting } from '@mnspro-cloud/services';
import {
  AbsenceReasonCategory,
  AbsenceResponse,
  AbsenceState,
} from '@mnspro-cloud/services/lib/mnspro-cloud/api/v2';
import moment from 'moment-timezone';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TagColorEnum, TagProps } from '../../../../Tags/Tags';
import {
  MultiLessonAbsenceCheckboxProps,
  TeachingAbsenceEntry,
} from '../../TeachingUnitEntry/TeachingAbsenceEntry';
import { formatAbsenceDate } from '../../../../Absences/common/formatAbsenceDate';
import { getAbsenceTagColor } from '../../../../Absences/common/getAbsenceTagColor';

export interface AbsenceStudentListEntryProps {
  absenceResponse: AbsenceResponse;
  preferenceSorting: UserListSorting;
  multiLessonIdsWithTimeSlotLabel?: MultiLessonIdWithTimeSlotLabel[];
  deleteAbsenceClick?: () => void;
  disabled?: boolean;
  saveAbsenceClick?: (item: AbsenceResponse) => void;
}
export interface MultiLessonIdWithTimeSlotLabel {
  timeSlotHeader?: string;
  timeSlotLabel?: string;
  lessonId: string;
}

/**
 * Represents a single student absence in a list context,
 * like the classbook grade/class/course views.
 */
export const AbsenceStudentListEntry: React.FC<
  AbsenceStudentListEntryProps
> = ({
  preferenceSorting,
  absenceResponse,
  multiLessonIdsWithTimeSlotLabel,
  deleteAbsenceClick,
  saveAbsenceClick,
  disabled,
}) => {
  const { t } = useTranslation();
  const initialCheckedLessonIds =
    multiLessonIdsWithTimeSlotLabel
      ?.map(a => a.lessonId)
      .filter(lessonId => absenceResponse.lessonIds.includes(lessonId)) ?? [];

  const [checkedLessonIds, setCheckedLessonIds] = useState(
    initialCheckedLessonIds,
  );

  const offset = absenceResponse.offset
    ? moment.duration(absenceResponse.offset).minutes()
    : 0;

  const color = getAbsenceTagColor(absenceResponse);

  const checkboxes =
    multiLessonIdsWithTimeSlotLabel?.map<MultiLessonAbsenceCheckboxProps>(
      a => ({
        checked: checkedLessonIds.includes(a.lessonId),
        timeSlotLabel: a.timeSlotLabel,
        timeSlotHeader: a.timeSlotHeader,
        disabled: disabled ?? false,
        onChecked: isChecked => {
          const isRadioButton =
            absenceResponse.absenceReason &&
            (absenceResponse.absenceReason.category ===
              AbsenceReasonCategory.late ||
              absenceResponse.absenceReason.category ===
                AbsenceReasonCategory.leftEarly);

          let checkedIds: string[];

          if (isRadioButton) {
            if (isChecked) {
              checkedIds = [a.lessonId];
            } else {
              checkedIds = [];
            }
          } else {
            checkedIds =
              isChecked &&
              (checkedLessonIds.find(b => b !== a.lessonId) ||
                checkedLessonIds.length === 0)
                ? [...checkedLessonIds, a.lessonId]
                : [...checkedLessonIds.filter(b => b !== a.lessonId)];
          }

          setCheckedLessonIds(checkedIds);

          if (saveAbsenceClick) {
            saveAbsenceClick({ ...absenceResponse, lessonIds: checkedIds });
          }
        },
      }),
    );

  const buttons =
    multiLessonIdsWithTimeSlotLabel && saveAbsenceClick && deleteAbsenceClick
      ? [
          {
            icon: <DeleteRegular />,
            onClick: deleteAbsenceClick,
            disabled,
            'aria-label': t('classbook:absences:delete_absence', { defaultValue: 'Absenz löschen' }),
          },
        ]
      : undefined;

  const tags: TagProps[] = [];
  if (offset > 0) {
    if (absenceResponse.absenceReason) {
      tags.push({
        label: absenceResponse.absenceReason.fullName ?? '',
        color,
      });
    }
    tags.push({
      label: `${offset} ${t('classbook:courseoverview:absence_minutes', { defaultValue: 'Minuten' })}`,
      color,
    });
  } else if (absenceResponse.absenceReason) {
    tags.push({
      label: absenceResponse.absenceReason.fullName ?? '',
      color,
    });
  }

  // The excuse type ("Attest eingereicht", …) exists as soon as the absence was
  // excused and is shown next to the reason, as on the student detail page of the
  // absences module.
  if (absenceResponse.absenceReason?.definition) {
    tags.push({
      label: absenceResponse.absenceReason.definition.shortName,
      color,
    });
  }

  if (
    !absenceResponse.absenceReason?.includeInStatistics &&
    absenceResponse.state !== AbsenceState.present &&
    absenceResponse.state !== AbsenceState.unchecked
  ) {
    tags.push({
      label: t('classbook:absences:absencereason_notincluded_statistic', { defaultValue: 'Stat. n. relev.' }),
      color: TagColorEnum.Yellow,
    });
  }

  const absenceText = useMemo(
    () => formatAbsenceDate(absenceResponse),
    [absenceResponse],
  );

  const description = t('classbook:absences:parralel', { 
    defaultValue: 'Eingetragen von {{teacher}} für {{timeslot}}', 
    teacher: absenceResponse.createdBy.displayName,      
    timeslot: absenceText, 
  });

  const absence = { ...absenceResponse, description };
  return (
    <TeachingAbsenceEntry
      updating={disabled}
      preferenceSorting={preferenceSorting}
      multiLessonAbsenceCheckbox={checkboxes}
      actions={buttons}
      readonly={
        multiLessonIdsWithTimeSlotLabel
          ? multiLessonIdsWithTimeSlotLabel?.length <
            absenceResponse.lessonIds.length
          : false
      }
      tags={tags}
      absenceResponse={absence}
      note={absenceResponse.description ?? undefined}
    />
  );
};
